import api from './api';

export type SiteStatus =
  | 'PROVISIONING'
  | 'INACTIVE'
  | 'ACTIVE'
  | 'SUSPENDED'
  | 'RETIRED';

export type SiteLifecycleAction = 'ACTIVATE' | 'RESUME' | 'RETIRE';

export interface PlatformSite {
  publicId: string;
  siteCode: string;
  siteName: string;
  siteStatus: SiteStatus;
  timezone: string;
  dataRegion: string | null;
  policyVersion: number;
  orgGraphVersion: number;
  placement: {
    type: 'SHARED_DB' | 'DEDICATED_DB';
    region: string;
    status: 'PREPARING' | 'ACTIVE' | 'MIGRATING' | 'FAILED' | 'RETIRED';
    version: number;
    schemaVersion: string;
  } | null;
  provisioning: {
    publicId: string;
    status: 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'RETIRED';
    currentStep: string | null;
    errorCode: string | null;
    errorMessage: string | null;
  } | null;
  createdAt: string;
  updatedAt: string;
}

export interface SiteReadinessReport {
  sitePublicId: string;
  siteStatus: SiteStatus;
  ready: boolean;
  evidenceDigest: string;
  checks: Array<{
    code: string;
    passed: boolean;
    message: string;
  }>;
}

export interface IsolationReadiness {
  multiSiteEnforcementReady: boolean;
  nonProductionTestMode: boolean;
  multiSiteOperationsAllowed: boolean;
  runtimeMode:
    | 'SINGLE_SITE'
    | 'ENFORCEMENT_READY'
    | 'NON_PRODUCTION_TEST';
  crossSiteSecondmentEnabled: boolean;
  evidenceDigest: string | null;
  minimumReleaseVersion: string | null;
  minimumSchemaVersion: string | null;
  endpointManifestDigest: string | null;
  approvedBy: number | null;
  approvedAt: string | null;
  changedAt: string;
  reason: string | null;
}

export interface PlatformUser {
  id: number;
  username: string;
  displayName: string;
}

export interface SiteLifecycleRequest {
  publicId: string;
  sitePublicId: string;
  siteCode: string;
  action: SiteLifecycleAction;
  status:
    | 'REQUESTED'
    | 'APPROVED'
    | 'EXECUTED'
    | 'REJECTED'
    | 'EXPIRED'
    | 'CANCELLED';
  requestedBy: number;
  approvedBy: number | null;
  evidenceDigest: string;
  reason: string;
  requestedAt: string;
  expiresAt: string;
  approvedAt: string | null;
  executedAt: string | null;
  errorCode: string | null;
}

export interface CreateSitePayload {
  siteCode: string;
  siteName: string;
  timezone: string;
  dataRegion: string;
  templateVersion: string;
  initialAdminUserId: number;
}

interface Envelope<T> {
  success: boolean;
  data: T;
}

export const platformSiteApi = {
  getReadiness: () =>
    api
      .get<Envelope<IsolationReadiness>>('/platform/site-readiness')
      .then((response) => response.data.data),

  listUsers: (search?: string) =>
    api
      .get<Envelope<PlatformUser[]>>('/platform/users', {
        params: search ? { search } : undefined,
      })
      .then((response) => response.data.data),

  listSites: () =>
    api
      .get<Envelope<PlatformSite[]>>('/platform/sites')
      .then((response) => response.data.data),

  createSite: (payload: CreateSitePayload, idempotencyKey: string) =>
    api
      .post<Envelope<PlatformSite>>('/platform/sites', payload, {
        headers: { 'Idempotency-Key': idempotencyKey },
      })
      .then((response) => response.data.data),

  updateSite: (
    sitePublicId: string,
    payload: { siteName?: string; timezone?: string },
  ) =>
    api
      .patch<Envelope<PlatformSite>>(`/platform/sites/${sitePublicId}`, payload)
      .then((response) => response.data.data),

  setInitialAdministrator: (sitePublicId: string, userId: number) =>
    api
      .put<Envelope<{ updated: boolean }>>(
        `/platform/sites/${sitePublicId}/initial-admin`,
        { userId },
      )
      .then((response) => response.data.data),

  validateSite: (sitePublicId: string, action: SiteLifecycleAction) =>
    api
      .post<Envelope<SiteReadinessReport>>(
        `/platform/sites/${sitePublicId}/validate`,
        { action },
      )
      .then((response) => response.data.data),

  requestLifecycle: (
    sitePublicId: string,
    action: SiteLifecycleAction,
    reason: string,
  ) => {
    const endpoint =
      action === 'ACTIVATE'
        ? 'activate'
        : action === 'RESUME'
          ? 'resume'
          : 'retire';
    return api
      .post<Envelope<SiteLifecycleRequest>>(
        `/platform/sites/${sitePublicId}/${endpoint}`,
        { reason },
      )
      .then((response) => response.data.data);
  },

  suspendSite: (sitePublicId: string, reason: string) =>
    api
      .post<Envelope<PlatformSite>>(
        `/platform/sites/${sitePublicId}/suspend`,
        { reason },
      )
      .then((response) => response.data.data),

  activateNonProductionTestSite: (sitePublicId: string, reason: string) =>
    api
      .post<Envelope<PlatformSite>>(
        `/platform/sites/${sitePublicId}/test-activate`,
        { reason },
      )
      .then((response) => response.data.data),

  listLifecycleRequests: () =>
    api
      .get<Envelope<SiteLifecycleRequest[]>>(
        '/platform/site-lifecycle-requests',
      )
      .then((response) => response.data.data),

  approveLifecycleRequest: (requestPublicId: string) =>
    api
      .post<Envelope<SiteLifecycleRequest>>(
        `/platform/site-lifecycle-requests/${requestPublicId}/approve`,
      )
      .then((response) => response.data.data),
};

export default platformSiteApi;
